/**
 * 389 (#1305) — what this device remembers about the home pop-up: the version the customer closed (so the same pop-up
 * is not shown again until staff change it), and which counts it already sent for a version (so each is sent once).
 *
 * Per device and browser, never sent anywhere. Every read and write is guarded, as `pwa/pushOptInDismissal.ts`: a throw
 * (Safari private mode, storage disabled) reads as nothing and a write is skipped. Showing the pop-up once too often is
 * a far smaller harm than failing to render the home page.
 */
import { RENTAL_ZONE } from '../utils/mytTime.js';

export const HOME_POPUP_CLOSED_KEY = 'carii:home-popup-closed-version';
/** 395: the Malaysia day and version of the last close, `YYYY-MM-DD <version>`, for "Once a day". */
export const HOME_POPUP_CLOSED_DAY_KEY = 'carii:home-popup-closed-day';
const sentKey = (version: string) => `carii:home-popup-sent:${version}`;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: nothing to remember.
  }
}

/** The version the customer last closed or used on this device, or null. */
export function closedVersion(): string | null {
  const v = read(HOME_POPUP_CLOSED_KEY);
  return v ? v : null;
}

/** The Malaysia calendar day of `at`, as `YYYY-MM-DD`. */
function malaysiaDay(at: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: RENTAL_ZONE }).format(at);
}

export function rememberClosed(version: string, at: Date = new Date()): void {
  write(HOME_POPUP_CLOSED_KEY, version);
  write(HOME_POPUP_CLOSED_DAY_KEY, `${malaysiaDay(at)} ${version}`);
}

/**
 * 395 (#1336): whether this device should show `version` now, by the frequency staff chose. "daily": not after a close of
 * this version on the same Malaysia day. "per_change": not after a close of this version (389's rule). Anything else,
 * including nothing, is "every visit": always (closing hides it for the current visit only, in the component).
 */
export function shouldShowPopup(
  frequency: string | undefined,
  version: string,
  at: Date = new Date(),
): boolean {
  if (frequency === 'per_change') return closedVersion() !== version;
  if (frequency === 'daily')
    return read(HOME_POPUP_CLOSED_DAY_KEY) !== `${malaysiaDay(at)} ${version}`;
  return true;
}

function sentList(version: string): string[] {
  const raw = read(sentKey(version));
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Whether this device already sent the count `kind` (`shown`, `seen:<poster>`, `tap:<poster>`) for `version`. */
export function hasSent(version: string, kind: string): boolean {
  return sentList(version).includes(kind);
}

export function markSent(version: string, kind: string): void {
  const list = sentList(version);
  if (list.includes(kind)) return;
  write(sentKey(version), JSON.stringify([...list, kind]));
}
